/**
 * Radar storage utilities for persisting user preferences
 */

const STORAGE_KEY_TIME_RANGE = 'radar_time_range_days'

export type RadarStorageType = 'teacher' | 'admin'

function timeRangeKey(radarType?: RadarStorageType | null): string {
  return radarType ? `${STORAGE_KEY_TIME_RANGE}:${radarType}` : STORAGE_KEY_TIME_RANGE
}

function parseDays(raw: string | null): number | null {
  if (raw === null) return null
  const parsed = parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : null
}
// bubblenew-v2 (E3): kollabierte Lehrer-Bündel im Admin-Radar.
// Unscoped = Legacy; scoped = `radar.admin.collapsed.v1:<userKey>`.
const STORAGE_KEY_ADMIN_COLLAPSED = 'radar.admin.collapsed.v1'

function adminCollapsedKey(userKey?: string | null): string {
  return userKey ? `${STORAGE_KEY_ADMIN_COLLAPSED}:${userKey}` : STORAGE_KEY_ADMIN_COLLAPSED
}

function parseCollapsedIds(raw: string | null): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((v): v is string => typeof v === 'string')
  } catch {
    return []
  }
}

/**
 * Default maximum time range for radar slider (in days)
 * Used as fallback when config is not loaded yet
 */
export const DEFAULT_MAX_TIME_RANGE_DAYS = 90

/**
 * Default time range for radar calculations (in days)
 * Used as fallback when timeRange prop is not provided
 */
export const DEFAULT_TIME_RANGE_DAYS = 60

/**
 * Get saved radar time range from localStorage
 * @returns Saved days or null if not saved/error
 */
export function getSavedTimeRangeDays(radarType?: RadarStorageType | null): number | null {
  try {
    if (radarType) {
      const scoped = parseDays(localStorage.getItem(timeRangeKey(radarType)))
      if (scoped != null) return scoped
      return parseDays(localStorage.getItem(STORAGE_KEY_TIME_RANGE))
    }
    return parseDays(localStorage.getItem(STORAGE_KEY_TIME_RANGE))
  } catch {
    return null
  }
}

/**
 * Save radar time range to localStorage
 * @param days - Number of days to save
 */
export function saveTimeRangeDays(days: number, radarType?: RadarStorageType | null): void {
  if (!Number.isFinite(days) || days < 1) return
  try {
    localStorage.setItem(timeRangeKey(radarType), String(days))
  } catch (error) {
    console.warn('[Radar] Could not save time range to localStorage:', error)
  }
}

/**
 * Clear saved radar time range from localStorage
 * Provided for future use (e.g., "Reset to default" button)
 */
export function clearSavedTimeRangeDays(radarType?: RadarStorageType | null): void {
  try {
    localStorage.removeItem(timeRangeKey(radarType))
  } catch (error) {
    console.warn('[Radar] Could not clear time range from localStorage:', error)
  }
}

/**
 * bubblenew-v2 (E3): IDs der Lehrer, deren Bündel im Admin-Radar kollabiert sind.
 * Persistenz pro Gerät + User (`radar.admin.collapsed.v1:<userKey>`).
 * Ohne userKey: Legacy-Key (Tests / nicht eingeloggt).
 * Einmal-Migration: existiert noch kein User-Key, wird der Legacy-Wert
 * übernommen und der unscoped Key gelöscht (erster User auf dem Gerät).
 */
export function getAdminCollapsedTeachers(userKey?: string | null): string[] {
  try {
    if (userKey) {
      const scopedRaw = localStorage.getItem(adminCollapsedKey(userKey))
      if (scopedRaw !== null) return parseCollapsedIds(scopedRaw)
      const legacy = parseCollapsedIds(localStorage.getItem(STORAGE_KEY_ADMIN_COLLAPSED))
      if (legacy.length > 0) {
        localStorage.setItem(adminCollapsedKey(userKey), JSON.stringify(legacy))
        localStorage.removeItem(STORAGE_KEY_ADMIN_COLLAPSED)
      }
      return legacy
    }
    return parseCollapsedIds(localStorage.getItem(STORAGE_KEY_ADMIN_COLLAPSED))
  } catch {
    return []
  }
}

export function setAdminCollapsedTeachers(teacherIds: string[], userKey?: string | null): void {
  try {
    localStorage.setItem(adminCollapsedKey(userKey), JSON.stringify(teacherIds))
  } catch (error) {
    console.warn('[Radar] Could not save collapsed teachers to localStorage:', error)
  }
}

/** Tote IDs entfernen. Leere active-Liste = noch kein Fetch → nicht wischen. */
export function pruneCollapsedTeachers(collapsed: string[], activeTeacherIds: string[]): string[] {
  if (activeTeacherIds.length === 0) return collapsed
  const active = new Set(activeTeacherIds)
  const pruned = collapsed.filter(id => active.has(id))
  return pruned.length === collapsed.length ? collapsed : pruned
}
